import { useState, useEffect, useCallback } from 'react';

export interface FavoritePlayer {
  id: number;
  name: string;
  photo: string;
  teamName: string;
  teamLogo: string;
  addedAt: number;
}

const STORAGE_KEY = 'footplayerstats_favorites';

export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoritePlayer[]>([]);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setFavorites(JSON.parse(stored));
      } catch {
        setFavorites([]);
      }
    }
  }, []);

  // Save favorites to localStorage whenever they change
  const saveFavorites = useCallback((newFavorites: FavoritePlayer[]) => {
    setFavorites(newFavorites);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newFavorites));
  }, []);

  const addFavorite = useCallback((player: Omit<FavoritePlayer, 'addedAt'>) => {
    setFavorites((prev) => {
      if (prev.some((p) => p.id === player.id)) return prev;
      const newFavorites = [...prev, { ...player, addedAt: Date.now() }];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newFavorites));
      return newFavorites;
    });
  }, []);

  const removeFavorite = useCallback((playerId: number) => {
    setFavorites((prev) => {
      const newFavorites = prev.filter((p) => p.id !== playerId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newFavorites));
      return newFavorites;
    });
  }, []);

  const toggleFavorite = useCallback((player: Omit<FavoritePlayer, 'addedAt'>) => {
    setFavorites((prev) => {
      const exists = prev.some((p) => p.id === player.id);
      const newFavorites = exists
        ? prev.filter((p) => p.id !== player.id)
        : [...prev, { ...player, addedAt: Date.now() }];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newFavorites));
      return newFavorites;
    });
  }, []);

  const isFavorite = useCallback((playerId: number) => {
    return favorites.some((p) => p.id === playerId);
  }, [favorites]);

  return {
    favorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
  };
}
